// src/RoadConditionsPage.js
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { db, storage } from './firebase.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// DriveNC / 511NC block iframe embedding (X-Frame-Options / CSP frame-ancestors).
// Opening them in a new tab is the supported way to view live DOT traffic.
const DRIVENC_SWAIN = 'https://drivenc.gov/?county=Swain';
const DRIVENC_MACON = 'https://drivenc.gov/?county=Macon';
const TRAFFIC_511NC =
  'https://www.511nc.org/map#/map?lat=35.248242&lon=-83.69559&z=11';

const NMR_CENTER = [35.248242, -83.69559];

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function RoadConditions({ user, isAdmin, isMobile }) {
  const [subdivisionDesc, setSubdivisionDesc] = useState('');
  const [images, setImages] = useState([]);
  const [editDesc, setEditDesc] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDesc();
  }, []);

  const fetchDesc = async () => {
    try {
      const descDoc = await getDoc(doc(db, 'road_conditions', 'subdivision'));
      if (descDoc.exists()) {
        setSubdivisionDesc(descDoc.data().description || 'No current road conditions reported.');
        setImages(descDoc.data().images || []);
      } else {
        setSubdivisionDesc('No current road conditions reported.');
        setImages([]);
      }
    } catch (err) {
      console.error('Error fetching road conditions:', err);
      setError('Failed to load road conditions');
      setSubdivisionDesc('No current road conditions reported.');
      setImages([]);
    }
  };

  const handleEdit = () => {
    setEditDesc(subdivisionDesc);
    setSelectedFiles([]);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!user || !isAdmin) return;
    try {
      let newImages = [...images];
      for (const file of selectedFiles) {
        const storageRef = ref(storage, `road_conditions/subdivision/images/${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newImages.push(url);
      }
      await setDoc(doc(db, 'road_conditions', 'subdivision'), {
        description: editDesc,
        images: newImages,
        updatedBy: user.uid,
        updatedAt: new Date()
      });
      setSubdivisionDesc(editDesc);
      setImages(newImages);
      setEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error saving road conditions:', err);
      setError('Failed to save road conditions');
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  return (
    <div className="road-conditions-page">
      {/* Hero Header */}
      <div className="road-hero">
        <div className="road-hero-content">
          <h1>Road Conditions &amp; Travel Info</h1>
          <p>
            Real-time updates for the roads that matter most around <strong>Nantahala Mountain Resort</strong>, 
            the Nantahala Gorge, and Western North Carolina.
          </p>
        </div>
      </div>

      <div className="road-content">
        {/* Quick Links */}
        <div className="road-section">
          <h2>Quick Links</h2>
          <div className="quick-links-grid">
            <a href="https://drivenc.gov" target="_blank" rel="noopener noreferrer" className="quick-link-card">
              <strong>DriveNC.gov</strong>
              <span>Official NC DOT road conditions &amp; closures</span>
            </a>
            <a href={TRAFFIC_511NC} target="_blank" rel="noopener noreferrer" className="quick-link-card">
              <strong>511NC Live Map</strong>
              <span>Real-time traffic, cameras &amp; incidents near Nantahala</span>
            </a>
            <a href={DRIVENC_MACON} target="_blank" rel="noopener noreferrer" className="quick-link-card">
              <strong>Macon County</strong>
              <span>Local road conditions</span>
            </a>
            <a href={DRIVENC_SWAIN} target="_blank" rel="noopener noreferrer" className="quick-link-card">
              <strong>Swain County</strong>
              <span>Bryson City &amp; Nantahala Gorge area</span>
            </a>
          </div>
        </div>

        {/* Regional map + external live traffic (DriveNC cannot be iframed) */}
        <div className="road-section">
          <h2>Regional Map &amp; Live Traffic</h2>
          <div className="road-traffic-actions">
            <a
              href={TRAFFIC_511NC}
              target="_blank"
              rel="noopener noreferrer"
              className="road-traffic-btn road-traffic-btn-primary"
            >
              Open 511NC Live Traffic Map →
            </a>
            <a
              href={DRIVENC_SWAIN}
              target="_blank"
              rel="noopener noreferrer"
              className="road-traffic-btn"
            >
              Open DriveNC (Swain County) →
            </a>
          </div>
          <p className="map-note road-traffic-embed-note">
            DriveNC and 511NC block embedding inside other websites (browser security headers), which is why an in-page
            iframe showed &ldquo;refused to connect.&rdquo; Use the buttons above for live DOT incidents, cameras, and closures.
          </p>
          <div className="map-container road-region-map">
            <MapContainer
              center={NMR_CENTER}
              zoom={11}
              scrollWheelZoom={false}
              style={{ height: isMobile ? 320 : 420, width: '100%', borderRadius: 12 }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={NMR_CENTER} icon={defaultIcon}>
                <Popup>Nantahala Mountain Resort</Popup>
              </Marker>
            </MapContainer>
          </div>
          <p className="map-note">
            Map centered on the Nantahala region (Swain / Macon). For official live traffic and closures, open 511NC or DriveNC above.
          </p>
        </div>

        {/* Key Local Routes */}
        <div className="road-section">
          <h2>Key Roads Around Nantahala</h2>
          <div className="routes-grid">
            <div className="route-card">
              <h4>US 74 / Great Smoky Mountains Expressway</h4>
              <p>Main route through Bryson City and the Nantahala Gorge. Watch for rockslides and construction.</p>
            </div>
            <div className="route-card">
              <h4>NC 28 (Nantahala Road)</h4>
              <p>Scenic route along the Nantahala River. Popular with visitors heading to the Outdoor Center.</p>
            </div>
            <div className="route-card">
              <h4>Wayah Road (NC 1310)</h4>
              <p>Beautiful mountain road to Franklin. Can be icy in winter and has tight curves.</p>
            </div>
            <div className="route-card">
              <h4>Access to Nantahala Outdoor Center</h4>
              <p>Via Wayah Road or US 74. Check conditions before heading out for rafting or lodging.</p>
            </div>
          </div>
        </div>

        {/* Local Resort Conditions */}
        <div className="road-section">
          <h2>Nantahala Mountain Resort Roads</h2>
          <div className="local-conditions-card">
            {editing ? (
              <div className="edit-form">
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  className="road-textarea"
                  rows="5"
                  placeholder="Describe current road conditions inside the resort..."
                />
                <div className="file-upload">
                  <label>Add photos of current conditions:</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  />
                </div>
                <div className="edit-actions">
                  <button onClick={handleSave} className="btn-primary">Save Updates</button>
                  <button onClick={handleCancel} className="btn-secondary">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p className="conditions-text">{subdivisionDesc}</p>
                
                {images.length > 0 && (
                  <div className="road-photos">
                    {images.map((url, index) => (
                      <img 
                        key={index} 
                        src={url} 
                        alt={`Road condition ${index + 1}`} 
                        className="road-photo" 
                      />
                    ))}
                  </div>
                )}
                
                {user && isAdmin && (
                  <button onClick={handleEdit} className="btn-primary mt-4">Update Conditions (Admin)</button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Winter & Safety Notes */}
        <div className="road-section">
          <h2>Travel Tips for the Nantahala Area</h2>
          <div className="tips-grid">
            <div className="tip-card">
              <h4>Winter Driving</h4>
              <p>Mountain roads can ice over quickly, even when it’s not snowing in the valleys. Carry chains or use 4WD/AWD.</p>
            </div>
            <div className="tip-card">
              <h4>Cell Service</h4>
              <p>Spotty coverage in the gorge and on many mountain roads. Download maps offline before traveling.</p>
            </div>
            <div className="tip-card">
              <h4>Rockslides &amp; Flooding</h4>
              <p>US 74 and NC 28 are prone to rockslides after heavy rain. Check DriveNC before heading out.</p>
            </div>
            <div className="tip-card">
              <h4>Leaf Season</h4>
              <p>October brings heavy traffic on scenic routes. Plan extra travel time.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoadConditions;